import React, { useState } from 'react';
import PromotionService from '../../services/promotion.service';
import FirebaseStorageService from '../../services/firebase-storage.service';
import { ICreatePromotionRequest } from '../../domain/interfaces/Promotions';

interface CreatePromotionFormProps {
  companyId: string;
  onPromotionCreated: () => void;
  onPreviewDataChange?: (data: { title: string; description: string; discount: number; imageUrl: string }) => void;
}

interface FormData {
  title: string;
  description: string;
  discount: number;
  imageUrl: string;
  endDate: string;
}

const CreatePromotionForm: React.FC<CreatePromotionFormProps> = ({
  companyId,
  onPromotionCreated,
  onPreviewDataChange
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    discount: 0,
    imageUrl: '',
    endDate: ''
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Manejar cambios en el formulario
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: name === 'discount' ? Number(value) : value
    };
    setFormData(newFormData);

    // Actualizar preview en tiempo real
    if (onPreviewDataChange) {
      onPreviewDataChange({
        title: newFormData.title,
        description: newFormData.description,
        discount: newFormData.discount,
        imageUrl: newFormData.imageUrl
      });
    }
  };

  // Manejar subida de imagen
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede ser mayor a 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      const imageUrl = await FirebaseStorageService.uploadFile(file, 'promotions');
      const newFormData = { ...formData, imageUrl };
      setFormData(newFormData);

      // Actualizar preview con nueva imagen
      if (onPreviewDataChange) {
        onPreviewDataChange({
          title: newFormData.title,
          description: newFormData.description,
          discount: newFormData.discount,
          imageUrl: newFormData.imageUrl
        });
      }
    } catch (err) {
      console.error('Error al subir imagen:', err);
      setError('Error al subir la imagen. Intenta nuevamente.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Validar formulario
  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setError('El título es requerido');
      return false;
    }
    if (!formData.description.trim()) {
      setError('La descripción es requerida');
      return false;
    }
    if (formData.discount <= 0 || formData.discount > 100) {
      setError('El descuento debe estar entre 1% y 100%');
      return false;
    }
    if (!formData.imageUrl) {
      setError('La imagen es requerida');
      return false;
    }
    if (!formData.endDate) {
      setError('La fecha de fin es requerida');
      return false;
    }
    return true;
  };

  // Enviar formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Convertir fechas a formato ISO 8601 con UTC      
      const endDateISO = new Date(formData.endDate + 'T23:59:59Z').toISOString();

      const promotionData: ICreatePromotionRequest = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        discount: formData.discount,
        endDate: endDateISO,
        imageUrl: formData.imageUrl,
        companyId: companyId
      };

      await PromotionService.createPromotion(promotionData);

      // Limpiar formulario
      setFormData({
        title: '',
        description: '',
        discount: 0,
        imageUrl: '',
        endDate: ''
      });

      // Notificar al componente padre
      onPromotionCreated();

    } catch (err) {
      console.error('Error al crear promoción:', err);
      setError('Error al crear la promoción. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg p-6">
      <h3 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-6">
        Crear Nueva Promoción
      </h3>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Título */}
        <div>
          <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
            Título de la Promoción *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent bg-white dark:bg-gray-700 text-xervix-text-primary dark:text-xervix-text-primary-dark"
            placeholder="Ej: ¡50% de descuento en todos nuestros servicios!"
            maxLength={100}
          />
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
            Descripción *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent bg-white dark:bg-gray-700 text-xervix-text-primary dark:text-xervix-text-primary-dark"
            placeholder="Describe los detalles de tu promoción..."
            maxLength={300}
          />
        </div>

        {/* Descuento */}
        <div>
          <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
            Porcentaje de Descuento *
          </label>
          <input
            type="number"
            name="discount"
            value={formData.discount || ''}
            onChange={handleInputChange}
            min="1"
            max="100"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent bg-white dark:bg-gray-700 text-xervix-text-primary dark:text-xervix-text-primary-dark"
            placeholder="Ej: 25"
          />
          <p className="text-xs text-xervix-text-secondary dark:text-xervix-text-secondary-dark mt-1">
            Ingresa un valor entre 1% y 100%
          </p>
        </div>

        {/* Imagen */}
        <div>
          <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
            Imagen de la Promoción *
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="block w-full text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-xervix-primary file:text-white hover:file:bg-red-500"
            />
            {uploadingImage && (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-xervix-primary"></div>
            )}
          </div>
          {formData.imageUrl && (
            <div className="mt-2">
              <img
                src={formData.imageUrl}
                alt="Preview"
                className="h-20 w-20 object-cover rounded-lg"
              />
            </div>
          )}
          <p className="text-xs text-xervix-text-secondary dark:text-xervix-text-secondary-dark mt-1">
            Tamaño máximo: 5MB. Formatos: JPG, PNG, GIF
          </p>
        </div>

        {/* Fechas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
              Fecha de Fin *
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-xervix-primary focus:border-transparent bg-white dark:bg-gray-700 text-xervix-text-primary dark:text-xervix-text-primary-dark"
            />
          </div>
        </div>

        {/* Botón de envío */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || uploadingImage}
            className="bg-xervix-primary hover:bg-red-500 text-white px-6 py-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>{loading ? 'Creando...' : 'Crear Promoción'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePromotionForm;
